import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { routes } from '../../../../../constants';
import { currentUserSelector, userActions } from '../../../../../state/modules/user';
import { noCapFilter, noCapMap } from '../../../../../utils';
import { FileUploadFormField, Icon, InputFormField, LoadingButton, SelectFormField, TextAreaFormField } from '../../../../components';
import Loading from '../../../loading';

const acceptableDocumentFileTypes = [
  'image/png',
  'image/jpeg',
  'text/csv',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]

const QualificationDetail = ({
  documentName,
  updateDocumentName,
  description,
  updateDescription,
  documentType,
  updateDocumentType,
  files,
  setFile,
  removeFile,
  addFile,
  existingFiles,
  removeExistingFile,
  submitForm,
  isUpdating,
  qualification,
  removeQualification,
  validation,
  qualificationTypes
}) => {

  const handleFileUpload = (index) => (acceptedFiles, rejectedFiles) => {
    const acceptedFile = fp.first(acceptedFiles);
    if (acceptedFile) {
      setFile(index, acceptedFile)
    }
  }

  const qualificationUpdated = !fp.equals(fp.get('name')(qualification))(documentName) ||
    !fp.equals(fp.get('description')(qualification))(description) ||
    !fp.equals(fp.get('attachments')(qualification))(existingFiles)

  const managerApprovalWarning = fp.get('approved')(qualification) ? `(Manager approval will be removed)` : ''

  return <Grid id='qualification-detail'>
    <h1 className='text-center'>
      <Icon icon='content-large' /> Qualification Detail
    </h1>

    <Row className='md-gutter'>
      <Col xs={12} md={8} mdOffset={2} className='boxed text-left'>
        <form>
          <Row className='top-gap'>
            <Col xs={12}>
              <SelectFormField
                label='Document Type'
                disabled={!!qualification}
                onChange={(e) => {
                  const qualificationType = parseInt(e.target.value, 10)
                  updateDocumentType(qualificationType ? qualificationType : null)
                }}
                value={documentType ? documentType : 'unspecified'}
                emptyOption={false}>
                {
                  fp.flow(
                    fp.map(({ id, name }) =>
                      <option key={id} value={id}>
                        {name}
                      </option>
                    )
                  )(qualificationTypes)
                }
                <option value='unspecified'>Other (please specify)</option>
              </SelectFormField>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <InputFormField {...{
                label: 'Document Name',
                onChange: (e) => { updateDocumentName(e.target.value) },
                value: documentName,
                validation: fp.get('documentName')(validation)
              }} />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <TextAreaFormField {...{
                label: 'Description (optional)',
                onChange: (e) => { updateDescription(e.target.value) },
                value: description,
                required: true
              }} />
            </Col>
          </Row>
          {
            [
              <Row key='upload-document-title' className='top-gap'>
                <Col xs={12}>
                  <p>Upload Document</p>
                </Col>
              </Row>,
              noCapMap(
                ({ url, internalFileName }, index) => {
                  return <Row key={`existing-files-${index}`} className='no-gutter qualification-document-upload'>
                    <Col xs={10} xsOffset={1}>
                      <a href={he.decode(url)} className='flex-center linkBox' target="_blank">
                        {internalFileName}
                      </a>
                    </Col>
                    <Col xs={1}>
                      <Icon id='cancel-upload-icon' iconSize={3} icon='close' onClick={() => removeExistingFile(index)} />
                    </Col>
                  </Row>
                }
              )(existingFiles),
              noCapMap(
                (fileUploaded, index) => {
                  return <Row key={`uploaded-files-${index}`} className='no-gutter qualification-document-upload'>
                    <Col xs={10} xsOffset={1}>
                      <FileUploadFormField
                        styleName={`file-${index}`}
                        multiple={false}
                        accept={acceptableDocumentFileTypes.join(',')}
                        onFileDrop={handleFileUpload(index)}
                        file={fileUploaded}
                        dropLabel='Upload Document (JPG, PNG, CSV, PDF, DOC, XLS)'
                      />
                    </Col>
                    <Col xs={1}>
                      <Icon id='cancel-upload-icon' iconSize={3} icon='close' onClick={() => removeFile(index)} />
                    </Col>
                  </Row>
                }
              )(files),
              <Row key='add-file-button' className='bottom-gap'>
                <Col xs={12} className='flex-center'>
                  <Button onClick={addFile}>+</Button>
                </Col>
              </Row>

            ]
          }

          <Row>
            {
              qualification ?
                <Col xs={12} className='top-gap'>
                  <LoadingButton className='full' disabled={!qualificationUpdated} loading={isUpdating} text={`Update ${managerApprovalWarning}`} onClick={submitForm} />
                </Col> :
                <Col xs={12} className='top-gap'>
                  <LoadingButton className='full' loading={isUpdating} text={'Create'} onClick={submitForm} />
                </Col>
            }
            {
              qualification ?
                <Col xs={12} className='top-gap'>
                  <LoadingButton className='full btn-delete' loading={isUpdating} text={'Delete'} onClick={removeQualification} />
                </Col> : null
            }
            <Col xs={12} className='top-gap'>
              <LinkContainer className='full' to={routes.app.professionalProfile.root}>
                <Button bsStyle='plain'>Cancel</Button>
              </LinkContainer>
            </Col>
          </Row>

        </form>
      </Col>
    </Row>
  </Grid>
}

const validate = ({ documentType, documentName }) => {
  const nameValidation = documentName.length > 0 ? null : "Qualification name is required"
  if (!nameValidation) {
    return null
  } else {
    return {
      documentName: nameValidation
    }
  }
}

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  isUpdating: state.user.isUpdating,
  isFetching: state.user.isFetchingQualifications,
  qualifications: state.user.qualifications,
  qualificationTypes: fp.get('qualificationTypes')(state.user.profile)
})
const mapDispatchToProps = (dispatch) => ({
  addQualification: bindActionCreators(userActions.user.addQualification, dispatch),
  updateQualification: bindActionCreators(userActions.user.updateQualification, dispatch),
  removeQualification: bindActionCreators(userActions.user.removeQualification, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ isFetching }) => isFetching,
    renderComponent(Loading)
  ),
  withProps(
    ({ qualifications, match }) => {
      const qualification = fp.flow(
        fp.find(
          ({ id }) => id === parseInt(match.params.qualificationId, 10)
        )
      )(qualifications)
      return {
        qualification
      }
    }
  ),
  withStateHandlers(
    ({ qualification }) => ({
      documentName: qualification ? qualification.name : '',
      description: qualification ? qualification.description : '',
      documentType: qualification ? qualification.qualificationTypeId : null,
      existingFiles: qualification ? qualification.attachments : '',
      files: [],
      validation: null
    }),
    {
      updateDocumentName: () => (documentName) => ({ documentName }),
      updateDescription: () => (description) => ({ description }),
      updateDocumentType: ({ documentName }, { qualificationTypes }) => (documentTypeId) => ({
        documentType: documentTypeId,
        documentName: fp.flow(
          fp.find(
            ({ id }) => id === documentTypeId
          ),
          fp.getOr('')('name')
        )(qualificationTypes)
      }),
      setFile: ({ files }) => (index, file) => ({
        files: noCapMap(
          (f, idx) => (idx === index) ? file : f
        )(files)
      }),
      removeFile: ({ files }) => (index) => ({
        files: noCapFilter(
          (f, idx) => idx !== index
        )(files)
      }),
      addFile: ({ files }) => () => ({
        files: files.concat(null)
      }),
      removeExistingFile: ({ existingFiles }) => (index) => ({
        existingFiles: noCapFilter(
          (f, idx) => idx !== index
        )(existingFiles)
      }),
      updateValidation: () => (validation) => ({
        validation
      })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      const { validation, updateValidation, documentType, documentName } = this.props
      if (validation) {
        const nextValidation = validate({
          documentType,
          documentName
        })
        if (!fp.isEqual(validation)(nextValidation)) {
          updateValidation(nextValidation)
        }
      }
    }
  }),
  withHandlers({
    submitForm: ({ currentUser, documentName, description, documentType, files, existingFiles, addQualification, qualification, updateQualification, updateValidation }) => () => {
      const validation = validate({
        documentType,
        documentName
      })
      if (validation) {
        updateValidation(validation)
      } else {
        const data = {
          userId: currentUser.id,
          name: documentName,
          description,
          qualificationTypeId: documentType,
          approved: false
        }
        const validFiles = fp.filter(f => f)(files)

        if (qualification) {
          updateQualification({
            ...data,
            id: qualification.id
          }, existingFiles, validFiles)
        } else {
          addQualification({ qualification: data }, validFiles)
        }
      }
    },
    removeQualification: ({ removeQualification, qualification }) => () => {
      removeQualification(qualification.id)
    }
  })
)(QualificationDetail)


// WEBPACK FOOTER //
// ./src/containers/pages/app/professional-profile/qualifications/detail.js